import { Outlet } from 'react-router-dom';
import { useSession } from '@/app/session/session';
import { beginLogin, ErrorContent } from '@/app/error/error-content';
import { ApiRequestError } from '@/shared/api/api-error';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { PageChromeProvider, usePageChromeValue } from './page-chrome';

/**
 * The application shell (UI spec §2): the fixed sidebar beside a sticky top bar
 * over a scrolling content region, laid out on the spec's `212px 1fr` grid.
 *
 * The shell is also the session boundary. It bootstraps `/api/me` once and holds
 * the Principal for the sidebar (which controls edit-gating) and the top bar
 * (which controls the `+ New initiative` action). An unauthenticated bootstrap
 * sends the user into the OIDC exchange rather than rendering an empty shell.
 */
export function AppShell(): React.JSX.Element {
  const session = useSession();

  // A 401 is authoritative even when a Principal is still cached: the session has
  // ended, whether by R1.5's idle/absolute caps or by a sign-out elsewhere (R1.6),
  // so the cached Principal is stale and the user belongs in the OIDC exchange.
  // Checked before the cache so neither of those can be papered over.
  if (session.error instanceof ApiRequestError && session.error.code === 'unauthenticated') {
    beginLogin();
    return <ShellSkeleton />;
  }

  const me = session.data;

  // Every other failure reaches the user only when there is no Principal to render
  // at all.
  //
  // This used to branch on `session.isError` first, which made the shell far more
  // fragile than the session it guards. TanStack Query keeps the last successful
  // `data` when a *refetch* fails and raises `isError` alongside it, so a single
  // failed `/api/me` refetch replaced the entire application - sidebar, top bar
  // and whichever screen was mounted - with the generic error screen, even though
  // a perfectly good Principal sat in the cache and the session on the server was
  // untouched. Only a full page reload cleared it, because a reload re-runs the
  // bootstrap on its own before any screen exists to compete with it. With a
  // Principal in hand the shell now stays up and a failing screen reports its own
  // failure in its own boundary, which is where a screen-level failure belongs.
  if (me === undefined) {
    return session.isError ? (
      <ErrorContent
        error={session.error}
        boundary="session-bootstrap"
        onRetry={() => void session.refetch()}
      />
    ) : (
      <ShellSkeleton />
    );
  }

  return (
    <PageChromeProvider>
      <div className="grid min-h-screen grid-cols-[212px_1fr]">
        <Sidebar me={me} />
        <div className="flex min-h-screen min-w-0 flex-col">
          <ShellTopBar />
          {/* pb-16 keeps the last row of content clear of the fixed humai badge. */}
          <main className="flex-1 overflow-x-auto px-[18px] pb-16 pt-4">
            <Outlet />
          </main>
        </div>
      </div>
    </PageChromeProvider>
  );
}

function ShellTopBar(): React.JSX.Element {
  const chrome = usePageChromeValue();
  const session = useSession();
  return <TopBar title={chrome.title} subtitle={chrome.subtitle} me={session.data} />;
}

function ShellSkeleton(): React.JSX.Element {
  return (
    <div className="grid min-h-screen grid-cols-[212px_1fr]">
      <div className="bg-navy h-screen w-[212px]" aria-hidden />
      <div className="bg-bg" aria-hidden />
    </div>
  );
}
